import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@/types/appRouter';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { showToast } from '@/components/Layout/Toast';
import { ConfirmationDialog } from '@/components/UI';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type GetPlanOutput = RouterOutputs['agent']['autopilot']['getPlan'];
type PlanData = GetPlanOutput['plan'];
type Blueprint = PlanData['blueprint'];
type PreflightOutput = RouterOutputs['agent']['autopilot']['preflightBlueprint'];

export function AutopilotActionBar(props: { planId: number; isBusy: boolean; onNavigateToSession: (sessionId: number) => void }) {
    const queryClient = useQueryClient();
    const [isLockConfirmOpen, setIsLockConfirmOpen] = useState(false);
    const [isLaunchConfirmOpen, setIsLaunchConfirmOpen] = useState(false);

    const planQuery = useQuery({
        ...trpc.agent.autopilot.getPlan.queryOptions({ planId: props.planId }),
        retry: false
    });

    const plan = planQuery.data?.success ? planQuery.data.plan : null;
    const blueprint: Blueprint | null = plan?.blueprint ?? null;

    const preflightQuery = useQuery({
        ...trpc.agent.autopilot.preflightBlueprint.queryOptions({ blueprint: blueprint! }),
        enabled: blueprint !== null,
        retry: false
    });

    const preflight: PreflightOutput | null = preflightQuery.data?.success ? preflightQuery.data : null;

    const invalidatePlanQueries = useCallback(async () => {
        await queryClient.invalidateQueries(trpc.agent.autopilot.getPlan.pathFilter());
        await queryClient.invalidateQueries(trpc.agent.autopilot.listPlans.pathFilter());
        await queryClient.invalidateQueries(trpc.agent.listSessionsEnriched.pathFilter());
    }, [queryClient]);

    const lockPlanMutation = useTrpcMutation(trpc.agent.autopilot.lockPlan.mutationOptions({ onSuccess: invalidatePlanQueries }), {
        fallbackErrorMessage: 'Failed to lock plan'
    });

    const startRunFromPlanMutation = useTrpcMutation(
        trpc.agent.autopilot.startRunFromPlan.mutationOptions({ onSuccess: invalidatePlanQueries }),
        {
            fallbackErrorMessage: 'Failed to start autopilot run'
        }
    );

    const scrollToPanel = useCallback(() => {
        const el = document.getElementById('autopilot-panel');
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    const planStatus = plan?.status ?? null;

    const preflightLabel = useMemo(() => {
        if (!preflight) return null;
        if (preflight.okToLock) return { text: 'Ready to lock', cls: 'text-green-400' };
        const blockersCount = preflight.blockers?.length ?? 0;
        return { text: blockersCount > 0 ? `${blockersCount} blocker${blockersCount === 1 ? '' : 's'}` : 'Blockers', cls: 'text-red-300' };
    }, [preflight]);

    const canLock = Boolean(planStatus === 'draft' && preflight?.okToLock);
    const canLaunch = Boolean(planStatus === 'locked');

    if (planQuery.isLoading) return null;
    if (!plan || !planStatus) return null;
    if (planStatus === 'archived') return null;

    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-900/40 px-3 py-2 flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-[11px] text-zinc-500">Autopilot Plan</div>
                    <div className="text-sm font-medium text-white truncate">{plan.name}</div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    {preflightLabel && <div className={`text-xs font-medium ${preflightLabel.cls}`}>{preflightLabel.text}</div>}
                    <div className="text-[11px] rounded bg-zinc-800 px-2 py-1 text-zinc-200">{planStatus}</div>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-0.5">
                {planStatus === 'draft' && (
                    <button
                        className="px-3 py-1.5 rounded-md bg-emerald-700 hover:bg-emerald-600 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-xs font-medium"
                        disabled={props.isBusy || lockPlanMutation.isPending || !canLock}
                        type="button"
                        onClick={() => {
                            if (!preflight) {
                                showToast('Preflight not ready yet — open the plan panel to review.', 'info');
                                scrollToPanel();
                                return;
                            }

                            if (!preflight.okToLock) {
                                showToast('Plan has blockers — open the plan panel to review.', 'warning');
                                scrollToPanel();
                                return;
                            }

                            setIsLockConfirmOpen(true);
                        }}
                    >
                        Lock Plan
                    </button>
                )}

                {planStatus === 'locked' && (
                    <button
                        className="px-3 py-1.5 rounded-md bg-fuchsia-700 hover:bg-fuchsia-600 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-xs font-medium"
                        disabled={props.isBusy || startRunFromPlanMutation.isPending || !canLaunch}
                        type="button"
                        onClick={() => setIsLaunchConfirmOpen(true)}
                    >
                        Launch Autopilot
                    </button>
                )}

                <button
                    className="px-3 py-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-medium"
                    type="button"
                    onClick={scrollToPanel}
                >
                    View Plan
                </button>
            </div>

            <ConfirmationDialog
                cancelText="Cancel"
                confirmText={lockPlanMutation.isPending ? 'Locking...' : 'Lock plan'}
                confirmVariant="primary"
                isOpen={isLockConfirmOpen}
                message={
                    <div className="space-y-2">
                        <div>
                            Locking will freeze the blueprint so Autopilot can run exactly what you planned. You can still start a run after
                            locking.
                        </div>
                        {preflight && preflight.warnings.length > 0 && (
                            <div className="text-sm text-yellow-300">
                                Warnings: {preflight.warnings.length} (see the plan panel for details)
                            </div>
                        )}
                    </div>
                }
                title="Lock Autopilot plan?"
                onCancel={() => setIsLockConfirmOpen(false)}
                onConfirm={() => {
                    setIsLockConfirmOpen(false);

                    void lockPlanMutation.mutateAsync({ planId: props.planId }).then((res) => {
                        if (!res.success) return;

                        if (res.okToLock) showToast('Plan locked', 'success');
                        else {
                            showToast('Plan could not be locked (preflight blockers)', 'warning');
                            scrollToPanel();
                        }
                    });
                }}
            />

            <ConfirmationDialog
                cancelText="Cancel"
                confirmText={startRunFromPlanMutation.isPending ? 'Starting...' : 'Launch autopilot'}
                confirmVariant="warning"
                isOpen={isLaunchConfirmOpen}
                message={
                    <div className="space-y-2">
                        <div>This will start a new Autopilot execution chat and run the locked plan.</div>
                        <div className="text-sm text-zinc-400">Budgets and consent are enforced by the plan.</div>
                    </div>
                }
                title="Launch Autopilot?"
                onCancel={() => setIsLaunchConfirmOpen(false)}
                onConfirm={() => {
                    setIsLaunchConfirmOpen(false);

                    void startRunFromPlanMutation.mutateAsync({ planId: props.planId }).then((res) => {
                        if (!res.success) return;

                        if (res.okToStart && 'sessionId' in res && typeof res.sessionId === 'number') {
                            showToast('Autopilot run queued', 'success');
                            props.onNavigateToSession(res.sessionId);
                        } else {
                            showToast('Autopilot run blocked (preflight blockers)', 'warning');
                            scrollToPanel();
                        }
                    });
                }}
            />
        </div>
    );
}
